import { useCallback, useMemo, useState } from 'react';
import {
  Box,
  Chip,
  IconButton,
  Switch,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import {
  GridColDef,
  GridPaginationModel,
  GridRowModel,
} from '@mui/x-data-grid';
import _ from 'lodash';

import { DEFAULT_ADMIN_ITEM_COUNT_PER_PAGE } from '@/config/constants';
import {
  useGetEmailsByAdminQuery,
  useRemoveEmailByAdminMutation,
  useToggleEmailByAdminMutation,
  useUpdateEmailByAdminMutation,
} from '@/apis/extended/admin/emailApi';
import { CommonDuplicateIconButton } from '@/components/Common/CommonButton/CommonDuplicateIconButton';
import { CommonEditIconButton } from '@/components/Common/CommonButton/CommonEditIconButton';
import { CommonRemoveIconButton } from '@/components/Common/CommonButton/CommonRemoveIconButton';
import { CommonDataGrid } from '@/components/Common/CommonDataGrid';
import { DataGridTextField } from '@/components/Common/CommonDataGrid/DataGridTextField';
import { AddEmailModal } from '@/components/ConfirmModals/AddEmailModal';
import { RemoveEmailModal } from '@/components/ConfirmModals/RemoveEmailModal';
import { Loader } from '@/components/Layout/Loader';
import { extractSpecialTags } from '@/utils/helper';
import { Email } from '@/types/EmailType';

interface ManageEmailTableProps {
  category?: 'contract' | 'interest_rate' | 'system' | 'marketing';
}

export const ManageEmailTable = ({ category }: ManageEmailTableProps) => {
  const theme = useTheme();

  const [offset, setOffset] = useState(0);
  const [limit, setLimit] = useState(DEFAULT_ADMIN_ITEM_COUNT_PER_PAGE);
  const [removingEmail, setRemovingEmail] = useState<Email | null>(null);
  const isRemoveEmailModalOpen = Boolean(removingEmail);
  const [updatingEmail, setUpdatingEmail] = useState<Email | null>(null);
  const isUpdateEmailModalOpen = Boolean(updatingEmail);
  const [duplicatingEmail, setDuplicatingEmail] =
    useState<Partial<Email> | null>(null);
  const isDuplicateEmailModalOpen = Boolean(duplicatingEmail);

  const { data, isLoading, isFetching } = useGetEmailsByAdminQuery({
    offset,
    limit,
    category,
  });
  const [updateEmailByAdmin] = useUpdateEmailByAdminMutation();
  const [removeEmailByAdmin] = useRemoveEmailByAdminMutation();
  const [toggleEmailByAdmin] = useToggleEmailByAdminMutation();

  const handleToggleActive = async (emailId: string) => {
    try {
      await toggleEmailByAdmin(emailId).unwrap();
    } catch (error) {
      console.error('Error toggling email:', error);
    }
  };

  const columns = useMemo(
    () =>
      [
        {
          field: 'key',
          headerName: 'Key',
          width: 180,
          editable: true,
          renderEditCell: (params) => {
            return <DataGridTextField {...params} />;
          },
        },
        {
          field: 'category',
          headerName: 'Category',
          width: 120,
          renderCell: (params) => {
            const categoryColors: Record<string, string> = {
              contract: '#1976d2',
              interest_rate: '#ed6c02',
              system: '#2e7d32',
              marketing: '#9c27b0',
              shopping: '#00acc1',
            };
            return (
              <Chip
                label={params.value || 'system'}
                size="small"
                sx={{
                  backgroundColor:
                    categoryColors[params.value || 'system'] || '#666',
                  color: 'white',
                  fontWeight: 500,
                }}
              />
            );
          },
        },
        {
          field: 'eventType',
          headerName: 'Event Type',
          width: 180,
          renderCell: (params) => {
            return params.value || '-';
          },
        },
        {
          field: 'contractStage',
          headerName: 'Contract Stage',
          width: 150,
          renderCell: (params) => {
            return params.value || '-';
          },
        },
        {
          field: 'targetRoles',
          headerName: 'Target Roles',
          width: 180,
          renderCell: (params) => {
            const roles = params.value || [];
            if (roles.length === 0) return '-';
            return (
              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                {roles.map((role: string) => (
                  <Chip
                    key={role}
                    label={role}
                    size="small"
                    variant="outlined"
                  />
                ))}
              </Box>
            );
          },
        },
        {
          field: 'title',
          headerName: 'Title',
          width: 280,
          editable: true,
          renderEditCell: (params) => {
            return <DataGridTextField {...params} />;
          },
        },
        {
          field: 'deepLinkTemplate',
          headerName: 'Deep Link',
          width: 200,
          renderCell: (params) => {
            if (!params.value) return '-';
            return (
              <Tooltip title={params.value}>
                <Typography
                  variant="body2"
                  sx={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {params.value}
                </Typography>
              </Tooltip>
            );
          },
        },
        {
          field: 'isActive',
          headerName: 'Active',
          width: 100,
          renderCell: (params) => {
            return (
              <Switch
                checked={params.value !== false}
                onChange={() => handleToggleActive(params.row._id)}
                size="small"
              />
            );
          },
        },
        {
          field: 'content',
          headerName: 'Tags',
          minWidth: 200,
          flex: 1,
          renderCell: (params) => {
            return extractSpecialTags(params.value);
          },
        },
        {
          field: 'actions',
          headerName: 'Actions',
          type: 'actions',
          width: 120,
          getActions: (params) => [
            <CommonDuplicateIconButton
              onClick={() =>
                setDuplicatingEmail({ ...params.row, _id: undefined })
              }
            />,
            <CommonEditIconButton
              onClick={() => setUpdatingEmail(params.row)}
            />,
            <CommonRemoveIconButton
              onClick={() => setRemovingEmail(params.row)}
            />,
          ],
        },
      ] as GridColDef<Email>[],
    [toggleEmailByAdmin]
  );

  const onPaginationChanged = ({ page, pageSize }: GridPaginationModel) => {
    setOffset(page * pageSize);
    setLimit(pageSize);
  };

  const processRowUpdate = useCallback(
    (newRow: GridRowModel, oldRow: GridRowModel) =>
      new Promise<GridRowModel>((resolve, reject) => {
        const isUpdated = !_.isEqual(newRow, oldRow);
        if (isUpdated) {
          updateEmailByAdmin({
            id: newRow._id,
            ..._.pick(newRow, [
              'key',
              'title',
              'category',
              'eventType',
              'contractStage',
              'targetRoles',
              'deepLinkTemplate',
              'description',
              'isActive',
            ]),
          })
            .unwrap()
            .then(() => resolve(newRow))
            .catch(() => resolve(oldRow));
        } else {
          resolve(oldRow);
        }
      }),
    [updateEmailByAdmin]
  );

  const handleRemoveEmail = () => {
    if (!removingEmail) return;
    removeEmailByAdmin(removingEmail._id);
    setRemovingEmail(null);
  };

  if (isLoading || !data) return <Loader isFull />;

  return (
    <>
      <CommonDataGrid
        columns={columns}
        rows={data.emails}
        rowCount={data.count}
        paginationModel={{ page: offset / limit, pageSize: limit }}
        onPaginationModelChange={onPaginationChanged}
        loading={isFetching}
        processRowUpdate={processRowUpdate}
      />
      <RemoveEmailModal
        isOpen={isRemoveEmailModalOpen}
        onClose={() => setRemovingEmail(null)}
        onConfirm={handleRemoveEmail}
      />
      <AddEmailModal
        isOpen={isUpdateEmailModalOpen}
        email={updatingEmail!}
        onClose={() => setUpdatingEmail(null)}
      />
      <AddEmailModal
        isOpen={isDuplicateEmailModalOpen}
        email={duplicatingEmail!}
        onClose={() => setDuplicatingEmail(null)}
      />
    </>
  );
};
